import { motion } from 'framer-motion'
import { BookOpen, Target, TrendingUp, MessageSquare, Handshake, GraduationCap, LucideIcon } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { features } from '@/data/mock-data'

const iconMap: Record<string, LucideIcon> = {
    BookOpen,
    Target,
    TrendingUp,
    MessageSquare,
    Handshake,
    GraduationCap,
}

export default function FeaturesSection() {
    return (
        <section className="py-24 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-30">
                <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary-blue/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-gold/10 rounded-full blur-3xl" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                {/* Section header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-4">
                        What We Offer
                    </span>
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                        Everything You Need to
                        <span className="block gradient-text">Crack NEET</span>
                    </h2>
                    <p className="text-lg text-theme-secondary max-w-2xl mx-auto">
                        Comprehensive features designed to accelerate your NEET preparation journey
                    </p>
                </motion.div>

                {/* Features grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((feature, index) => {
                        const Icon = iconMap[feature.icon]
                        return (
                            <motion.div
                                key={feature.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                            >
                                <GlassmorphicCard className="p-6 h-full hover:scale-105 hover:shadow-2xl hover:shadow-primary-blue/20 transition-all duration-300 group cursor-pointer">
                                    <div className="flex flex-col h-full">
                                        <div className="p-3 rounded-xl bg-primary-blue/20 text-primary-blue w-fit mb-4 group-hover:bg-primary-blue group-hover:text-white transition-all duration-300">
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <h3 className="text-xl font-display font-semibold text-theme-primary mb-3 group-hover:text-primary-blue transition-colors">
                                            {feature.title}
                                        </h3>
                                        <p className="text-theme-muted text-sm leading-relaxed flex-1">
                                            {feature.description}
                                        </p>
                                        <div className="mt-4 flex items-center text-primary-blue text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                            Learn more
                                            <svg className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                </GlassmorphicCard>
                            </motion.div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}
